import type {
  CourseData,
  DeckIR,
  FidelityReport,
  MediaAsset,
  ParseDiagnostic,
  PlayerCapabilities,
  PublishedCourse,
  PublishedMedia,
  PublishedSlide,
  Interaction,
} from '@slideforge/shared';
import {
  COURSE_FORMAT_VERSION,
  activeTakeOf,
  compileSlide,
  textBodyToPlainText,
} from '@slideforge/shared';

/**
 * Turns the parsed deck plus the author's course data into `course.json` —
 * the single document every delivery target hands the player.
 */
export interface CompileCourseOptions {
  readonly deck: DeckIR;
  readonly course: CourseData;
  readonly diagnostics: readonly ParseDiagnostic[];
  readonly capabilities: PlayerCapabilities;
  readonly generatorVersion: string;
  /** Fixed by the caller so two builds of the same deck differ in one field only. */
  readonly publishedAt: string;
  readonly fidelity: FidelityReport;
  readonly availableFonts?: ReadonlySet<string>;
}

export interface CompileCourseResult {
  readonly course: PublishedCourse;
  /** Media actually referenced by the compiled slides, keyed by asset id. */
  readonly usedMedia: readonly MediaAsset[];
  readonly droppedBySlideIndex: ReadonlyMap<number, readonly string[]>;
  /**
   * Narration clips the package must contain, keyed by the audio store's
   * content hash. The publisher resolves the bytes; compiling only decides
   * which takes are actually used.
   */
  readonly usedNarration: ReadonlyMap<string, {
    readonly href: string;
    readonly mimeType: string;
    readonly captionsHref?: string;
    readonly captions?: readonly { readonly startMs: number; readonly endMs: number; readonly text: string }[];
  }>;
}

export function compileCourse(options: CompileCourseOptions): CompileCourseResult {
  const { deck, course } = options;
  const mediaByAsset = new Map(deck.media.map((m) => [m.id, m]));
  const used = new Set<string>();
  const dropped = new Map<number, readonly string[]>();
  const narrationClips = new Map<string, {
    href: string;
    mimeType: string;
    captionsHref?: string;
    captions?: readonly { startMs: number; endMs: number; text: string }[];
  }>();
  let hasSyntheticNarration = false;

  const mediaHref = (mediaId: string): string => {
    const asset = mediaByAsset.get(mediaId);
    if (!asset) return '';
    used.add(mediaId);
    return `media/${mediaId}.${asset.extension}`;
  };

  const slides: PublishedSlide[] = deck.slides
    .filter((slide) => !slide.hidden)
    .map((slide) => {
      const compiled = compileSlide(slide, {
        deck,
        mediaHref,
        slideHref: (slideId) => `#slide-${slideId}`,
        ...(options.availableFonts ? { fontFallback: (f) => f } : {}),
      });
      dropped.set(slide.index, compiled.dropped);

      const quiz = course.slides[slide.id]?.quiz;
      const interaction = course.slides[slide.id]?.interaction;
      const publishedInteraction = interaction ? inheritDeckTheme(interaction, deck, slide.layoutIndex) : undefined;
      if (publishedInteraction) markInteractionMedia(publishedInteraction.items, mediaHref);

      // Only the active take ships. Alternate takes are authoring history and
      // would silently multiply the package size.
      const take = activeTakeOf(course.slides[slide.id]?.narration);
      let narration;
      if (take) {
        const href = `narration/${take.audioId}.${extensionForAudio(take.mimeType)}`;
        const captions = course.slides[slide.id]?.narration?.captions ?? [];
        const captionsHref = captions.length > 0 ? `captions/${take.audioId}.vtt` : undefined;
        narrationClips.set(take.audioId, {
          href,
          mimeType: take.mimeType,
          ...(captionsHref ? { captionsHref, captions } : {}),
        });
        if (take.source === 'tts') hasSyntheticNarration = true;
        narration = {
          href,
          mimeType: take.mimeType,
          durationMs: take.durationMs,
          ...(captionsHref ? { captions, captionsHref } : {}),
        };
      }

      return {
        id: slide.id,
        index: slide.index,
        title: slideTitle(slide),
        kind: quiz ? ('quiz' as const) : publishedInteraction ? ('interaction' as const) : ('content' as const),
        notes: slide.notes ? textBodyToPlainText(slide.notes) : '',
        render: compiled.slide,
        ...(quiz ? { quiz } : {}),
        ...(publishedInteraction ? { interaction: publishedInteraction } : {}),
        ...(narration ? { narration } : {}),
        transitionMs: slide.transition?.durationMs ?? 0,
        ...(slide.transition?.advanceAfterMs !== undefined
          ? { advanceAfterMs: slide.transition.advanceAfterMs }
          : {}),
      };
    });

  const media: PublishedMedia[] = [...used]
    .map((id) => mediaByAsset.get(id))
    .filter((a): a is MediaAsset => a !== undefined)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((asset) => ({
      id: asset.id,
      href: `media/${asset.id}.${asset.extension}`,
      mimeType: asset.mimeType,
      byteLength: asset.byteLength,
    }));

  return {
    course: {
      formatVersion: COURSE_FORMAT_VERSION,
      courseId: course.courseId,
      title: course.title || deck.title,
      description: course.description,
      language: course.language,
      stageWidth: slides[0]?.render.width ?? 1280,
      stageHeight: slides[0]?.render.height ?? 720,
      player: course.player,
      completion: course.completion,
      capabilities: options.capabilities,
      slides,
      media,
      fidelity: options.fidelity,
      syntheticNarration: hasSyntheticNarration && course.tts.syntheticDisclosure,
      generator: { name: 'SlideForge', version: options.generatorVersion },
      publishedAt: options.publishedAt,
    },
    usedMedia: media.map((m) => mediaByAsset.get(m.id)).filter((a): a is MediaAsset => a !== undefined),
    droppedBySlideIndex: dropped,
    usedNarration: narrationClips,
  };
}

function inheritDeckTheme(interaction: Interaction, deck: DeckIR, layoutIndex: number): Interaction {
  if (interaction.theme.colorSource !== 'inherit-from-deck') return interaction;
  const layout = deck.layouts[layoutIndex];
  const master = layout ? deck.masters[layout.masterIndex] : undefined;
  const theme = master ? deck.themes[master.themeIndex] : deck.themes[0];
  if (!theme) return interaction;
  return {
    ...interaction,
    theme: {
      ...interaction.theme,
      // Marking the compiled copy as custom freezes the chosen deck colours in
      // the package; the authoring model remains "inherit" and follows future
      // theme changes on the next publish.
      colorSource: 'custom',
      palette: {
        accent: cssHex(theme.colorScheme.accent1),
        surface: cssHex(theme.colorScheme.lt1),
        text: cssHex(theme.colorScheme.dk1),
      },
    },
  };
}

function cssHex(value: string): string {
  return value.startsWith('#') ? value : `#${value}`;
}

function markInteractionMedia(
  items: readonly { readonly media: { readonly assetId?: string }; readonly children: readonly unknown[] }[],
  mediaHref: (mediaId: string) => string,
): void {
  for (const item of items) {
    if (item.media.assetId) mediaHref(item.media.assetId);
    markInteractionMedia(
      item.children as readonly { readonly media: { readonly assetId?: string }; readonly children: readonly unknown[] }[],
      mediaHref,
    );
  }
}

/**
 * A file extension for a MediaRecorder MIME type.
 *
 * `audio/webm;codecs=opus` has to become `.webm`, not `.webm;codecs=opus`, or
 * every LMS that serves by extension hands the browser the wrong content type.
 */
function extensionForAudio(mimeType: string): string {
  const base = mimeType.split(';')[0]?.trim().toLowerCase() ?? '';
  if (base === 'audio/webm') return 'webm';
  if (base === 'audio/ogg') return 'ogg';
  if (base === 'audio/mp4') return 'm4a';
  if (base === 'audio/mpeg') return 'mp3';
  if (base === 'audio/wav' || base === 'audio/x-wav') return 'wav';
  return 'bin';
}

/**
 * The outline label. A slide's title placeholder is the authored answer; the
 * first line of any text on the slide is a better fallback than "Slide 7".
 */
function slideTitle(slide: DeckIR['slides'][number]): string {
  for (const shape of slide.shapes) {
    if (shape.kind !== 'shape' || !shape.text) continue;
    const isTitle = shape.placeholder?.type === 'title' || shape.placeholder?.type === 'ctrTitle';
    if (!isTitle) continue;
    const text = textBodyToPlainText(shape.text).trim();
    if (text) return firstLine(text);
  }
  for (const shape of slide.shapes) {
    if (shape.kind !== 'shape' || !shape.text) continue;
    const text = textBodyToPlainText(shape.text).trim();
    if (text) return firstLine(text);
  }
  return slide.name || `Slide ${slide.index + 1}`;
}

function firstLine(text: string): string {
  const line = text.split('\n')[0] ?? text;
  return line.length > 90 ? `${line.slice(0, 87)}...` : line;
}
